import Extension from "../base/extension";

/**
 * Shows an add-to-cart popup for any active `cross_sell_alert` metaobject
 * entry that the just-added product qualifies for, unless the cart already
 * contains a product from that alert's "combine with" collection or the
 * alert has already been shown this cart.
 *
 * Config comes from `window.crossSellAlertsConfig`, rendered once globally
 * by snippets/cross-sell-alert-config.liquid - see that file for why
 * collection membership is embedded there rather than checked via an API.
 *
 * Multiple alerts can legitimately match the same add-to-cart action (e.g. a
 * Fibo board qualifies for both a Fibo accessories AND a Mereva showers
 * cross-sell), so matches are queued and shown one at a time rather than
 * picking a single winner.
 *
 * The popup's product carousel shows the added product's `m_p.related_products`
 * that are in the alert's "combine with" collection, falling back to that
 * collection's products when the metafield is empty or none of them qualify.
 */
class CrossSellAlerts extends Extension {
  name = "CrossSellAlerts";
  alerts = [];
  queue = [];
  showing = false;
  modalEl;
  modal;

  constructor(name) {
    super(name);
  }

  init() {
    this.alerts = window.crossSellAlertsConfig || [];
    if (this.alerts.length === 0) return;

    this.modalEl = document.querySelector('[data-cross-sell-alert-modal]');
    if (!this.modalEl || typeof bootstrap === 'undefined') return;

    this.modal = new bootstrap.Modal(this.modalEl);
    this.modalEl.addEventListener('hidden.bs.modal', () => {
      this.showing = false;
      this.showNextQueued();
    });

    document.addEventListener('cart:added', this.handleCartAdded.bind(this));
    document.addEventListener('cart:cleared', this.handleCartCleared.bind(this));
    document.addEventListener('cart:rendered', this.handleCartMayBeEmpty.bind(this));
    document.addEventListener('minicart:rendered', this.handleCartMayBeEmpty.bind(this));
  }

  async handleCartAdded(e) {
    // Product/variant IDs come back as JS numbers from the Cart API, but
    // as strings from Liquid's `| json` on the config script side - compare
    // everything as strings to avoid type-mismatched `.includes()` checks.
    const addResponse = e.detail.addResponse;
    const addedItems = Array.isArray(addResponse)
      ? addResponse
      : (addResponse.items || [addResponse]);
    const addedProductIds = addedItems.map(item => String(item.product_id));

    // Start fetching each added product's related-product slides alongside the
    // cart fetch, so the popup isn't held up waiting for them afterwards.
    addedItems.forEach(item => this.getRelatedSlides(item.url));

    const cart = await fetch(`${routes.cart_url}.js`).then(response => response.json());
    const cartProductIds = cart.items.map(item => String(item.product_id));
    const dismissedGuids = this.getDismissedGuids(cart);

    const toQueue = this.alerts.filter(alert => {
      const guid = String(alert.guid);
      if (dismissedGuids.includes(guid)) return false;
      const wasTriggered = addedProductIds.some(id => alert.triggerProductIds.map(String).includes(id));
      if (!wasTriggered) return false;
      const alreadyCombined = cartProductIds.some(id => alert.combineWithProductIds.map(String).includes(id));
      return !alreadyCombined;
    });

    if (toQueue.length === 0) return;

    // Remember which added product triggered each alert, so its popup can show
    // that product's related products rather than the whole collection.
    this.queue.push(...toQueue.map(alert => ({
      alert,
      triggerUrl: addedItems.find(item => alert.triggerProductIds.map(String).includes(String(item.product_id)))?.url
    })));
    this.persistDismissed(toQueue.map(alert => String(alert.guid)), dismissedGuids);
    this.showNextQueued();
  }

  /**
   * Fetches the product's `m_p.related_products` as upsell slides (see
   * sections/cross-sell-related-products.liquid), given the added line item's
   * `url` (/products/{handle}?variant=...). Cached per product; resolves to an
   * empty array if the product has none or the request fails.
   */
  getRelatedSlides(productUrl) {
    if (!productUrl) return Promise.resolve([]);
    const productPath = productUrl.split('?')[0];
    this.relatedSlidesCache ||= {};

    this.relatedSlidesCache[productPath] ||= fetch(`${productPath}?section_id=cross-sell-related-products`)
      .then(response => response.ok ? response.text() : '')
      .then(html => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-cross-sell-related-products] > .swiper-slide')])
      .catch(() => []);

    return this.relatedSlidesCache[productPath];
  }

  getDismissedGuids(cart) {
    const raw = cart.attributes && cart.attributes.cross_sell_dismissed;
    return raw ? raw.split(',').filter(Boolean) : [];
  }

  persistDismissed(newGuids, existingGuids) {
    const merged = Array.from(new Set([...existingGuids, ...newGuids]));
    Stencil.Extensions.Cart.setCartAttributes({ cross_sell_dismissed: merged.join(',') });
  }

  async showNextQueued() {
    if (this.showing || this.queue.length === 0) return;
    const { alert, triggerUrl } = this.queue.shift();
    this.showing = true;

    // Prefer the triggering product's own related products, but only the ones
    // the discount actually applies to (i.e. in the "combine with" collection),
    // since the popup copy promises the discount.
    const combineWithIds = alert.combineWithProductIds.map(String);
    const eligibleRelatedSlides = (await this.getRelatedSlides(triggerUrl))
      .filter(slide => combineWithIds.includes(slide.dataset.productId));

    this.modalEl.querySelector('[data-cross-sell-alert-modal-headline]').textContent = alert.popupHeadline;
    this.modalEl.querySelector('[data-cross-sell-alert-modal-body]').textContent = alert.popupBody;

    const cta = this.modalEl.querySelector('[data-cross-sell-alert-modal-cta]');
    cta.textContent = alert.popupCtaText;
    cta.href = alert.combineWithCollectionUrl;

    // Swap in the product swiper for this alert. The template (rendered by
    // cross-sell-alert-swiper-templates.liquid) contains a <swiper-slider>
    // whose `options` attribute is read in its own connectedCallback, so
    // cloning it into a connected container self-initialises the carousel -
    // no manual `new Swiper(...)` call needed here. Its slides are the
    // "combine with" collection's products, replaced with the eligible related
    // products (if any) before it's connected, so Swiper initialises on those.
    const swiperContainer = this.modalEl.querySelector('[data-cross-sell-alert-modal-swiper]');
    swiperContainer.innerHTML = '';
    const swiperTemplate = document.getElementById(`cross-sell-alert-swiper-${alert.guid}`);
    if (swiperTemplate) {
      const swiperFragment = swiperTemplate.content.cloneNode(true);
      if (eligibleRelatedSlides.length > 0) {
        swiperFragment.querySelector('.swiper-wrapper')
          .replaceChildren(...eligibleRelatedSlides.map(slide => slide.cloneNode(true)));
      }
      swiperContainer.appendChild(swiperFragment);
    }

    this.modal.show();
  }

  handleCartCleared() {
    this.clearDismissed();
  }

  handleCartMayBeEmpty(e) {
    if (e.detail.cart && e.detail.cart.item_count === 0) {
      this.clearDismissed();
    }
  }

  clearDismissed() {
    Stencil.Extensions.Cart.clearCartAttributes(['cross_sell_dismissed']);
  }
}

export default CrossSellAlerts;
